import React, { useEffect, useRef, useState, useMemo } from 'react';
import moment from 'moment';
import {
  Fade,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation, matchPath } from 'react-router-dom';
import classNames from 'classnames';
import SpeakingIcon from '@src/assets/icons/speaking-icon.svg';
import SubtitleCaptionIcon from '@src/assets/icons/subtitle-caption.svg';
import CommonTypography from '@src/new-components/Typography';
import ClientPauseNew from '@src/containers/TTSNew/ClientPauseNew';
import { DOCUMENT_LINK, LANDING_PAGE_URL } from '@src/configs';
import actions from '@src/redux/actions';
import { useTheme } from '@mui/styles';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useExportHistoryEnabled from '@src/hooks/useExportHistoryEnabled';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { useCheckProductName } from '@src/hooks/useCheckProduct';
import {
  checkVoucherByProductForHeaderBanner,
  verifyTime,
} from '@src/services/voucher';
import apis from '@src/apis';
import {
  useCheckProjectRoute,
  useCheckTTSRoute,
} from '@src/hooks/useCheckDubbingRoute';
import { MOBILE_BREAKPOINT } from '@src/constants';
import SpeakerSettings from '@src/containers/ProjectDetail/Dubbing/SpeakerSettings';
import { COLOR } from '@src/styles/color';
import { CONVERTING_STATUS, PROJECT_PRODUCT } from '@src/constants/project';
import { DUBBING_PANEL_VIEW } from '@src/constants/dubbing';
import {
  selectEveryTextBlockSynthesized,
  selectHasSynthesizingBlock,
} from '@src/redux/project/selectors';
import { getExportingState } from '@src/services/project';
import { useClickOutside } from '@src/hooks/useClickOutSide';
import NewDictionary from '@src/containers/NewDictionary';
import useSidebarRoutes from '@src/hooks/useSidebarRoutes';
import MovieFilterGradient from '@src/assets/images/movie-filter-gradient.svg';
import { PopoverMenuItem } from '@src/containers/ProjectDetail/component/ProjectDetailHeader/index.style';
import {
  StyledBetaWrapper,
  StyledDrawerNew,
  StyledSidebarTooltip,
  StyledSubMenu,
  StyledTagNewWrapper,
} from './index.style';
import SmallSidebar from './SmallSidebar';
import LargeSidebar from './LargeSidebar';

const NEW_SUPPORT_MENU = [
  {
    label: 'userGuide',
    href: DOCUMENT_LINK,
    isActive: true,
  },
  {
    label: 'supportPage',
    href: `${LANDING_PAGE_URL}/support`,
    isActive: true,
  },
];

const DesktopSidebar = ({
  openSidebar,
  handleOpenSidebar,
  handleCloseSidebar,
  ipLocation,
  useHeaderBanner,
  headerVoucher,
}) => {
  const sidebarBodyRef = useRef(null);
  const [openSubMenu] = useState(false);
  const [, setShowScrollbar] = useState(false);
  const { getFeatureValue } = useFeatureFlags();
  const [vouchers, setVouchers] = useState([]);
  const [showAutoRenewalBanner, setShowAutoRenewalBanner] = useState(false);
  const [openDictionary, setOpenDictionary] = useState(false);
  const [openClientPause, setOpenClientPause] = useState(false);
  const [openSpeakerSetting, setOpenSpeakerSetting] = useState(false);
  const [anchorFormatOption, setAnchorFormatOption] = useState(null);
  const [openFormatOption, setOpenFormatOption] = useState(false);
  const [voucherSidebar, setVoucherSidebar] = useState([]);
  const refClickOutSide = useClickOutside(() => setOpenFormatOption(false));

  const handleOpenDictionary = () => setOpenDictionary(true);
  const handleCloseDictionary = () => setOpenDictionary(false);

  const handleOpenClientPause = () => setOpenClientPause(true);
  const handleCloseClientPause = () => setOpenClientPause(false);

  const handleOpenSpeakerSetting = () => setOpenSpeakerSetting(true);
  const handleCloseSpeakerSetting = () => setOpenSpeakerSetting(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  // Swaps the editor panel instead of opening a dialog.
  const dubbingPanelView = useSelector(
    (state) => state.layout.dubbingPanelView,
  );
  const isCaptionPanelOpen = dubbingPanelView !== DUBBING_PANEL_VIEW.BLOCK_LIST;
  const handleOpenCaptionPanel = () => {
    if (isCaptionPanelOpen) return;
    dispatch(
      actions.layout.setDubbingPanelView(DUBBING_PANEL_VIEW.CAPTION_STYLES),
    );
  };

  const { user } = useSelector((state) => state.auth);
  const { usingPackage, subscriptionPurchase, lastExpiredOrder } = useSelector(
    (state) => state.user,
  );
  const { lockCharacters, identityProvider } = useSelector(
    (state) => state.auth.user,
  );
  // Field-by-field, and the two block-wide questions as derived booleans. The
  // whole-slice form re-rendered the entire sidebar on every keystroke in the
  // editor — and re-scanned the whole block map three times to reach the same
  // two answers.
  const audioType = useSelector((state) => state.project.audioType);
  const status = useSelector((state) => state.project.status);
  const projectProduct = useSelector((state) => state.project.product);
  const isProjectSynthesizing = useSelector(
    (state) => state.project.isProjectSynthesizing,
  );
  const isLoadingProjectDetail = useSelector(
    (state) => state.project.isLoadingProjectDetail,
  );
  const exports = useSelector((state) => state.project.exports);
  const isDownloading = useSelector((state) => state.project.isDownloading);
  const hasBlockSynthesis = useSelector(selectHasSynthesizingBlock);
  const everyBlockSynthesisSuccess = useSelector(
    selectEveryTextBlockSynthesized,
  );
  const projectSynthesisSuccess = status === CONVERTING_STATUS.SUCCESS;
  const isDubbingProjectSynthesizing =
    projectProduct === PROJECT_PRODUCT.DUBBING && isProjectSynthesizing;

  // Exporting used to freeze the audio type and the speaker setting. It no
  // longer does: the backend snapshots the project's blocks and the export
  // config when the request is created, so a change made afterwards cannot
  // reach the file being produced. Behind the export-history flag, so the
  // previous behaviour is one switch away.
  const exportHistoryEnabled = useExportHistoryEnabled();
  const exportBannerEnabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_EXPORT_VIDEO,
    { userId: user.id, email: user.email, phoneNumber: user.phoneNumber },
  );
  const { isExporting } = getExportingState({
    exports,
    isDownloading,
    product: projectProduct,
  });
  const isExportLocked =
    exportBannerEnabled && isExporting && !exportHistoryEnabled;

  // Speaker config reassigns the voice on blocks, and a download is assembled
  // from those blocks as they stand — changing one mid-flight ships a file that
  // doesn't match the project.
  //
  // Reads `isExporting` rather than `isExportLocked`: that one is additionally
  // gated on the export-banner flag, which decides whether the BANNER shows,
  // not whether an export is running. Note `isExporting` already folds in
  // `isDownloading` — on its own that flag only covers the synchronous
  // downloads (the SRT built in the browser); audio and video run through the
  // async export pipeline and leave it false for their whole run.
  // `hasBlockSynthesis` alongside the project-wide flag: a per-block create-audio
  // reads the block's voiceCode and never raises `isProjectSynthesizing`, so on
  // that flag alone this route stays open while a synthesis is reading the very
  // voices it changes. Same pair the `projectAudioType` route is gated on.
  const isSpeakerSettingLocked =
    isDubbingProjectSynthesizing ||
    hasBlockSynthesis ||
    (isExporting && !exportHistoryEnabled);

  const presentationExtractProgressEnabled =
    projectProduct === PROJECT_PRODUCT.PRESENTATION &&
    getFeatureValue(FEATURE_KEYS.PRESENTATION_EXTRACT_PROGRESS, {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    });

  const product = useCheckProductName();

  const checkCharacterReservationPeriod = () => {
    let preservedCharacterDate;

    preservedCharacterDate = user.preservationExpiryDate;
    if (user.preservationExpiryDate)
      preservedCharacterDate = moment(user.preservationExpiryDate);
    else if (user.packageExpiryDate) {
      preservedCharacterDate = moment(user.packageExpiryDate).add(30, 'days');
    } else if (lastExpiredOrder?.id) {
      const expiryIn =
        lastExpiredOrder.customPackage?.expiresIn ||
        lastExpiredOrder.package?.expiresIn;
      preservedCharacterDate = moment(lastExpiredOrder.confirmedAt)
        .add(expiryIn, 'days')
        .add(30, 'days');
    }

    if (!preservedCharacterDate) return false;
    return moment().isBefore(moment(preservedCharacterDate));
  };

  const hasLockCharacters =
    useCheckTTSRoute() &&
    lockCharacters > 0 &&
    checkCharacterReservationPeriod();

  const activeRoute = (routePath) =>
    matchPath(location.pathname, { path: routePath, exact: true });

  const { desktopSidebarRoutes, detailProjectRoutes } = useSidebarRoutes();
  const isDetailProjectPage = useCheckProjectRoute();
  const menuItems = isDetailProjectPage
    ? detailProjectRoutes
    : desktopSidebarRoutes;

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md')); // width >= 900px
  const isMiddleScreen = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px <= width < 900px

  const drawerProperties = {
    sx: {
      display: {
        xs: 'none',
        sm: isMiddleScreen ? 'block' : 'none',
        md: isLargeScreen ? 'block' : 'none',
      },
    },
    variant: openSidebar && isMiddleScreen ? 'temporary' : 'persistent',
    anchor: 'left',
    onClose: isMiddleScreen ? handleCloseSidebar : null,
    ModalProps: isMiddleScreen
      ? {
          keepMounted: true,
          // At this width the drawer is a temporary one, and the open prop
          // below is hardcoded, so its modal stays open for as long as the
          // window stays this size. An open modal keeps a focus trap, and
          // crossing into this width remounts it on top of any dialog already
          // showing — from where it pulled focus straight back out of every
          // field in that dialog, a moment after each click.
          disableEnforceFocus: true,
        }
      : {},
  };

  const isExpiredPackage =
    !usingPackage.id ||
    (user.packageExpiryDate && moment().isAfter(user.packageExpiryDate));

  const handleOpenAutoRenewalPayment = () => {
    setShowAutoRenewalBanner(false);
  };
  const renderGradient = () => (
    <svg style={{ position: 'absolute' }} width={0} height={0}>
      <linearGradient
        id="linearColors"
        x1={-4.17647}
        y1={2.67476}
        x2={22.2053}
        y2={8.03194}
        gradientUnits="userSpaceOnUse"
      >
        <stop offset={0} stopColor="#f48b71" />
        <stop offset={0.502423} stopColor="#f1414d" />
        <stop offset={1} stopColor="#563efa" />
      </linearGradient>
    </svg>
  );

  const MenuItemCollapsedSidebarMultiBlockUIV2 = ({ route, isActiveRoute }) => {
    const dictionaryIcon = useMemo(
      () => (
        <img
          src={SpeakingIcon}
          alt="Speaking Icon"
          style={{ width: 24, height: 24 }}
        />
      ),
      [],
    );

    let menuIcon = useMemo(
      () => (
        <>
          {route.isGradient && renderGradient()}
          <route.iconSidebar
            className={classNames('item-icon', {
              'item-icon-active': isActiveRoute,
            })}
            sx={{ fill: route.isGradient && 'url(#linearColors)' }}
          />
        </>
      ),
      [route, isActiveRoute],
    );

    if (route?.name === 'projectAudioType') {
      menuIcon = useMemo(
        () => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              gap: '8px',
              width: '100%',
            }}
            className="format-option"
          >
            <div
              style={{ height: '24px', display: 'flex', alignItems: 'center' }}
            >
              {audioType}
            </div>
            <CommonTypography color={COLOR.navyBlue} variant="body-12-medium">
              {t('formatAudio')}
            </CommonTypography>
          </div>
        ),
        [route, isActiveRoute, audioType, t],
      );
    }
    if (
      route?.name === 'projectClientPause' ||
      route?.name === 'speakerSetting'
    ) {
      menuIcon = useMemo(
        () => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              gap: '8px',
              width: '100%',
            }}
            className="format-option"
          >
            {route?.iconSidebar && (
              <route.iconSidebar
                className={classNames('item-icon', {
                  'item-icon-active': isActiveRoute,
                })}
              />
            )}
            <CommonTypography color={COLOR.navyBlue} variant="body-12-medium">
              {route?.name === 'projectClientPause'
                ? t('pauseSettings')
                : t('speakerSetting')}
            </CommonTypography>
          </div>
        ),
        [route, isActiveRoute, t],
      );
    }
    if (route?.name === 'projectDictionary') {
      menuIcon = useMemo(
        () => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              width: '100%',
              gap: '8px',
            }}
            className="format-option"
          >
            {dictionaryIcon}
            <CommonTypography color={COLOR.navyBlue} variant="body-12-medium">
              {t('pronunciationV2')}
            </CommonTypography>
          </div>
        ),
        [route, isActiveRoute, t],
      );
    }
    if (route?.name === 'projectCaption') {
      menuIcon = (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            gap: '4px',
            width: '100%',
          }}
          className="format-option"
        >
          <img
            src={SubtitleCaptionIcon}
            alt=""
            style={{ width: 24, height: 24 }}
          />
          <CommonTypography color={COLOR.navyBlue} variant="body-12-medium">
            {t('projectCaption')}
          </CommonTypography>
        </div>
      );
    }
    return <>{menuIcon}</>;
  };

  const MenuItemCollapsedSidebar = ({ route, isActiveRoute }) => {
    let menuIcon = (
      <>
        {route.isGradient && renderGradient()}
        <route.iconSidebar
          className={classNames('item-icon', {
            'item-icon-active': isActiveRoute,
          })}
          sx={{ fill: route.isGradient && 'url(#linearColors)' }}
        />
      </>
    );
    if (route?.name === 'project') {
      menuIcon = <img src={MovieFilterGradient} alt="project" />;
    }
    if (route?.name === 'projectAudioType') {
      menuIcon = <div className="format-option">{audioType}</div>;
    }
    if (route?.name === 'projectDictionary') {
      menuIcon = (
        <img
          src={SpeakingIcon}
          alt="Speaking Icon"
          style={{ width: 24, height: 24 }}
        />
      );
    }
    return (
      <StyledSidebarTooltip
        title={openFormatOption ? '' : t(route?.labelSidebar)}
      >
        {menuIcon}
      </StyledSidebarTooltip>
    );
  };

  const MenuItemExpandedSidebar = ({ route, isActiveRoute }) => {
    if (route?.name === 'projectDictionary') {
      return (
        <img
          src={SpeakingIcon}
          alt="Speaking Icon"
          style={{ width: 24, height: 24 }}
        />
      );
    }
    if (route?.name === 'projectCaption') {
      return (
        <img
          src={SubtitleCaptionIcon}
          alt=""
          style={{ width: 24, height: 24 }}
        />
      );
    }
    return (
      <>
        {route.isGradient && renderGradient()}
        <route.iconSidebar
          className={classNames('item-icon', {
            'item-icon-active': isActiveRoute,
          })}
          sx={{ fill: route.isGradient && 'url(#linearColors)' }}
        />
      </>
    );
  };

  const UIMenuItemCollapsedSidebar = isDetailProjectPage
    ? MenuItemCollapsedSidebarMultiBlockUIV2
    : MenuItemCollapsedSidebar;

  const MenuItemContent = ({ route, isActiveRoute }) =>
    openSidebar ? (
      <>
        <MenuItemExpandedSidebar route={route} isActiveRoute={isActiveRoute} />
        <div className="item-wrapper">
          <ListItemText
            primary={t(route?.labelSidebar)}
            disableTypography
            className={classNames('item-text', {
              'item-text-active': isActiveRoute,
              'item-text-gradient': route.isGradient,
            })}
          />
          {route.beta && (
            <StyledBetaWrapper active={isActiveRoute}>
              <Typography className="beta-text">Beta</Typography>
            </StyledBetaWrapper>
          )}
          {route.isNewFeature && (
            <StyledTagNewWrapper>
              <CommonTypography variant="body-12-medium">
                {t('new')}
              </CommonTypography>
            </StyledTagNewWrapper>
          )}
        </div>
      </>
    ) : (
      <UIMenuItemCollapsedSidebar route={route} isActiveRoute={isActiveRoute} />
    );

  const createLinks = (aRoutes, props) =>
    aRoutes.map((route) => {
      const hasEnabledRoute = route.featureKey
        ? getFeatureValue(route.featureKey, {
            userId: user?.id,
            email: user?.email,
            phoneNumber: user?.phoneNumber,
          })
        : true;
      if (!hasEnabledRoute) return null;

      // Subtitles have no route, so the panel says if it is active.
      const isActiveRoute =
        route?.name === 'projectCaption'
          ? isCaptionPanelOpen
          : activeRoute(route.path) ||
            route.subPaths?.some((path) => activeRoute(path));

      if (route?.externalUrl) {
        return (
          <ListItem
            key={route?.labelSidebar}
            className="item"
            onMouseEnter={() => setShowScrollbar(true)}
            onMouseLeave={() => setShowScrollbar(false)}
          >
            <a
              href={route.path}
              target="_blank"
              rel="noopener noreferrer"
              className={classNames('item-link', {
                'active-link': isActiveRoute,
              })}
              onClick={() => {
                if (props?.mobile) handleCloseSidebar();
              }}
            >
              {openSidebar ? (
                <>
                  <route.iconSidebar
                    className={classNames('item-icon', {
                      'item-icon-active': isActiveRoute,
                    })}
                  />
                  <div className="item-wrapper">
                    <ListItemText
                      primary={t(route?.labelSidebar)}
                      disableTypography
                      className={classNames('item-text', {
                        'item-text-active': isActiveRoute,
                      })}
                    />
                    {route.beta && (
                      <StyledBetaWrapper active={isActiveRoute}>
                        <div className="beta-background" />
                        <Typography className="beta-text">Beta</Typography>
                      </StyledBetaWrapper>
                    )}
                    {route.isNewFeature && (
                      <StyledBetaWrapper active={isActiveRoute}>
                        <div className="beta-background" />
                        <Typography className="beta-text">New</Typography>
                      </StyledBetaWrapper>
                    )}
                  </div>
                </>
              ) : (
                <StyledSidebarTooltip title={t(route?.labelSidebar)}>
                  <route.iconSidebar className="item-icon" />
                </StyledSidebarTooltip>
              )}
            </a>
          </ListItem>
        );
      }
      const nameMenuProjectDetails = [
        'projectClientPause',
        'projectDictionary',
        'projectAudioType',
        'speakerSetting',
        'projectCaption',
      ];

      const handleChangeAudioType = (type) => {
        if (isExportLocked) return;
        const equalType = type === audioType;
        if (
          (everyBlockSynthesisSuccess || projectSynthesisSuccess) &&
          !equalType
        ) {
          dispatch(actions.project.setOpenDialogChangeFormat(true));
        } else {
          dispatch(actions.project.setAudioType(type));
        }
      };

      const isProjectDetailRoute = nameMenuProjectDetails.includes(route.name);
      const hasProductLoading = [
        PROJECT_PRODUCT.PRESENTATION,
        PROJECT_PRODUCT.DUBBING,
      ].includes(projectProduct);
      const showLoadingOverlay =
        isProjectDetailRoute &&
        isDetailProjectPage &&
        hasProductLoading &&
        isLoadingProjectDetail &&
        !presentationExtractProgressEnabled;

      return (
        <ListItem
          key={route?.labelSidebar}
          ref={refClickOutSide}
          className={classNames('item', {
            'border-bottom': isDetailProjectPage,
          })}
          onMouseEnter={() => setShowScrollbar(true)}
          onMouseLeave={() => setShowScrollbar(false)}
          disabled={
            showLoadingOverlay ||
            (route?.name === 'projectAudioType' &&
              (hasBlockSynthesis || isExportLocked)) ||
            (route?.name === 'speakerSetting' && isSpeakerSettingLocked)
          }
          onClick={(e) => {
            if (showLoadingOverlay) return;
            if (route?.name === 'projectClientPause') handleOpenClientPause();
            if (route?.name === 'projectDictionary') handleOpenDictionary();
            if (route?.name === 'projectCaption') handleOpenCaptionPanel();
            if (route?.name === 'speakerSetting' && !isSpeakerSettingLocked)
              handleOpenSpeakerSetting();
            if (
              route?.name === 'projectAudioType' &&
              !hasBlockSynthesis &&
              !isExportLocked &&
              projectProduct !== PROJECT_PRODUCT.DUBBING
            ) {
              setAnchorFormatOption(e.currentTarget);
              setOpenFormatOption(true);
            }
          }}
        >
          {nameMenuProjectDetails.includes(route.name) ? (
            <NavLink
              data-id={route.dataId}
              className={classNames('item-link', {
                'active-link': isActiveRoute,
                'disabled-audio-type':
                  projectProduct === PROJECT_PRODUCT.DUBBING &&
                  route.name === 'projectAudioType',
                'audio-type-link': route.name === 'projectAudioType',
                'nav-link-multi-block-uiv2': isDetailProjectPage,
              })}
              to="#"
              style={{
                cursor:
                  ((hasBlockSynthesis || isExportLocked) &&
                    route.name === 'projectAudioType') ||
                  (route?.name === 'speakerSetting' && isSpeakerSettingLocked)
                    ? 'not-allowed'
                    : 'pointer',
                filter: showLoadingOverlay ? 'blur(8px)' : 'none',
                pointerEvents: showLoadingOverlay ? 'none' : undefined,
              }}
            >
              <MenuItemContent route={route} isActiveRoute={isActiveRoute} />
            </NavLink>
          ) : (
            <NavLink
              to={isDetailProjectPage ? '#' : route.path}
              className={classNames('item-link', {
                'active-link': isActiveRoute,
              })}
              data-id={route.dataId}
              onClick={() => {
                if (props?.callback) props.callback();
              }}
            >
              <MenuItemContent route={route} isActiveRoute={isActiveRoute} />
            </NavLink>
          )}
          <Popper
            transition
            style={{
              zIndex: 2000,
              width: '100px',
              color: '#1E164E',
              boxShadow: '0px 0px 20px 2px rgba(0, 0, 0, 0.1)',
            }}
            anchorEl={anchorFormatOption}
            open={openFormatOption}
            placement="right-start"
          >
            {({ TransitionProps }) => (
              <Fade
                style={{ padding: '4px 0px', boxShadow: 'none' }}
                {...TransitionProps}
                timeout={350}
              >
                <Paper>
                  {['mp3', 'wav'].map((type) => (
                    <PopoverMenuItem
                      key={type}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChangeAudioType(type);
                        setOpenFormatOption(false);
                      }}
                      active={audioType === type}
                      style={{
                        color: '#1E164E',
                        fontWeight: 500,
                        fontSize: 14,
                        lineHeight: '20px',
                        letterSpacing: '-0.4px',
                        verticalAlign: 'middle',
                      }}
                    >
                      <span>.{type}</span>
                    </PopoverMenuItem>
                  ))}
                </Paper>
              </Fade>
            )}
          </Popper>
        </ListItem>
      );
    });

  const renderSubMenuSupport = ({ props, className, itemClassName }) => (
    <StyledSubMenu className={className}>
      <List component="div" disablePadding>
        {NEW_SUPPORT_MENU.filter((item) => item.isActive).map((item) => (
          <a
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className={`item-link ${itemClassName}`}
            onClick={() => {
              if (props?.mobile) handleCloseSidebar();
            }}
          >
            <Typography className="item-text">{t(item.label)}</Typography>
          </a>
        ))}
      </List>
    </StyledSubMenu>
  );

  useEffect(() => {
    if (openSubMenu) {
      // Scroll to bottom when open sub menu on small screen
      // Delay to scroll to bottom when open sub menu on small screen to wait for the sub menu to be rendered
      setTimeout(() => {
        const sidebarBody = sidebarBodyRef.current;
        sidebarBody.lastElementChild.scrollIntoView({
          behavior: 'smooth',
        });
      }, 200);
    }
  }, [openSubMenu]);

  const getSatisfiedVouchers = (
    voucherData,
    isUseHeaderBanner,
    isUseVoucherByProduct,
  ) => {
    const newVouchers = voucherData.reduce((acc, currVoucher) => {
      const satisfiedTime = verifyTime(currVoucher);
      if (!satisfiedTime) return acc;

      const canUse =
        satisfiedTime &&
        currVoucher?.remainingAmount > 0 &&
        !currVoucher.isPrivate &&
        currVoucher.usedAmount < currVoucher.numReuse;
      return [...acc, { ...currVoucher, satisfiedTime, canUse }];
    }, []);

    const sortVouchers = newVouchers.sort((voucher) =>
      voucher.canUse ? -1 : 1,
    );

    setVouchers(sortVouchers);
    if (isUseVoucherByProduct && sortVouchers.length > 0) {
      const canUseVoucher = sortVouchers.filter(
        (voucher) =>
          voucher.canUse &&
          checkVoucherByProductForHeaderBanner({ voucher, product }),
      );
      const checkVoucherForLockCharacterInListVouchers = canUseVoucher.find(
        (voucher) => voucher.isPreserveVoucher,
      );
      if (hasLockCharacters && !checkVoucherForLockCharacterInListVouchers) {
        canUseVoucher.push({
          canUse: true,
          isPreserveVoucher: true,
        });
      }
      const checkMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      let finalHeaderVoucher = [];
      if (!checkMobile) {
        finalHeaderVoucher = canUseVoucher.filter(
          (item) => item.isHeaderBanner,
        );
      } else {
        finalHeaderVoucher = [...canUseVoucher];
      }
      if (finalHeaderVoucher.length > 0 && isUseHeaderBanner) {
        dispatch(actions.voucher.updateHeaderVoucher(finalHeaderVoucher));
      } else {
        dispatch(actions.voucher.updateHeaderVoucher(undefined));
      }
    } else if (!isUseVoucherByProduct && sortVouchers.length > 0) {
      const flashSaleVoucher = sortVouchers.filter(
        (voucher) => voucher.isHeaderBanner && voucher.canUse,
      );
      if (
        flashSaleVoucher &&
        flashSaleVoucher.length > 0 &&
        isUseHeaderBanner
      ) {
        dispatch(actions.voucher.updateHeaderVoucher([flashSaleVoucher[0]]));
      } else {
        dispatch(actions.voucher.updateHeaderVoucher(undefined));
      }
    } else {
      dispatch(actions.voucher.updateHeaderVoucher(undefined));
    }
  };

  const fetchVouchers = async (isUseHeaderBanner, isUseVoucherByProduct) => {
    const data = await apis.vouchers.getVouchers();
    const dataSidebar = await apis.vouchers.getBannerVouchersSidebar();

    if (dataSidebar.status) {
      setVoucherSidebar(dataSidebar?.result?.banners || []);
    }
    if (data.status)
      getSatisfiedVouchers(
        data.result,
        isUseHeaderBanner,
        isUseVoucherByProduct,
      );
  };

  useEffect(() => {
    handleOpenAutoRenewalPayment();
  }, [usingPackage, subscriptionPurchase]);

  // Fetch vouchers by product
  useEffect(() => {
    fetchVouchers(useHeaderBanner, true);
  }, [product, useHeaderBanner]);

  const renderSidebar = () => {
    if (openSidebar) {
      return (
        <LargeSidebar
          identityProvider={identityProvider}
          useHeaderBanner={useHeaderBanner}
          headerVoucher={headerVoucher}
          createLinks={createLinks}
          sidebarRoute={menuItems}
          activeRoute={activeRoute}
          ipLocation={ipLocation}
          isExpiredPackage={isExpiredPackage}
          handleCloseSidebar={handleCloseSidebar}
          renderSubMenuSupport={renderSubMenuSupport}
          vouchers={vouchers}
          showAutoRenewalBanner={showAutoRenewalBanner}
          voucherSidebar={voucherSidebar}
        />
      );
    }

    return (
      <SmallSidebar
        identityProvider={identityProvider}
        useHeaderBanner={useHeaderBanner}
        headerVoucher={headerVoucher}
        createLinks={createLinks}
        sidebarRoute={menuItems}
        activeRoute={activeRoute}
        ipLocation={ipLocation}
        isExpiredPackage={isExpiredPackage}
        handleCloseSidebar={handleCloseSidebar}
        handleOpenSidebar={handleOpenSidebar}
        renderSubMenuSupport={renderSubMenuSupport}
        vouchers={vouchers}
        voucherSidebar={voucherSidebar}
        showAutoRenewalBanner={showAutoRenewalBanner}
      />
    );
  };

  return (
    <>
      <StyledDrawerNew
        sx={drawerProperties.sx}
        variant={drawerProperties.variant}
        anchor={drawerProperties.anchor}
        open
        opensidebar={openSidebar}
        onClose={drawerProperties.onClose}
        ModalProps={drawerProperties.ModalProps}
        widthCollapsed={isDetailProjectPage && 96}
      >
        {renderSidebar()}
      </StyledDrawerNew>
      {openDictionary && (
        <NewDictionary open={openDictionary} onClose={handleCloseDictionary} />
      )}
      <ClientPauseNew
        open={openClientPause}
        onClose={handleCloseClientPause}
        isProject
      />
      {openSpeakerSetting && (
        <SpeakerSettings
          open={openSpeakerSetting}
          onClose={handleCloseSpeakerSetting}
        />
      )}
    </>
  );
};

export default DesktopSidebar;
